import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { ArticleEditor } from "./ArticleEditor";

export const ArticlesTab = () => {
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/articles").then((r) => setArticles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startEdit = async (slug) => {
    const res = await api.get(`/articles/${slug}`);
    setEditing(res.data);
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/articles/${a.slug}`);
      toast.success("Article deleted.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-articles-tab">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight">Insight Notes</h2>
          <p className="mt-1 text-sm text-slate-500">{articles.length} published · changes go live on the site immediately.</p>
        </div>
        {!editing && (
          <button data-testid="admin-new-article-button" onClick={() => setEditing("new")} className="flex items-center gap-2 bg-[#6D28D9] px-4 py-2.5 font-mono2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#FFFFFF] transition-colors hover:bg-[#5B21B6]">
            <Plus className="h-3.5 w-3.5" /> New note
          </button>
        )}
      </div>

      {editing && (
        <div className="mt-8">
          <ArticleEditor article={editing === "new" ? null : editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
        </div>
      )}

      <div className="mt-8 space-y-px border border-slate-200 bg-slate-200">
        {articles.map((a) => (
          <div key={a.slug} data-testid={`admin-article-row-${a.slug}`} className="flex flex-col gap-3 bg-[#F6F4FD] px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-4">
              {a.cover_image && (
                <img data-testid={`admin-article-thumb-${a.slug}`} src={fileUrl(a.cover_image)} alt="" className="hidden h-12 w-16 shrink-0 border border-slate-200 object-cover sm:block" />
              )}
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="border border-[#6D28D9]/30 px-2 py-0.5 font-mono2 text-[9px] uppercase tracking-[0.2em] text-[#6D28D9]">{a.category}</span>
                  <span className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-500">{a.date} · {a.read_time}</span>
                </div>
                <p className="mt-1.5 truncate font-medium text-slate-800">{a.title}</p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Link to={`/insights/${a.slug}`} target="_blank" data-testid={`admin-article-view-${a.slug}`} className="flex items-center gap-1.5 border border-slate-200 px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-500 transition-colors hover:text-[#6D28D9]">
                <ExternalLink className="h-3 w-3" /> View
              </Link>
              <button data-testid={`admin-article-edit-${a.slug}`} onClick={() => startEdit(a.slug)} className="flex items-center gap-1.5 border border-slate-200 px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-700 transition-colors hover:border-[#6D28D9]/40 hover:text-[#6D28D9]">
                <Pencil className="h-3 w-3" /> Edit
              </button>
              <button data-testid={`admin-article-delete-${a.slug}`} onClick={() => remove(a)} className="flex items-center gap-1.5 border border-slate-200 px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-500 transition-colors hover:border-rose-600/40 hover:text-rose-700">
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
